import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, throwError, of } from 'rxjs';
import { switchMap, map, share } from 'rxjs/operators';
import {
  FiatCreditCardDepositRedirectResponse,
  FiatDeposit,
  FiatDepositFee,
  FiatDepositType,
  PaymentGatewayConfirmationInput,
  PaymentType,
  PaymentTypeInfo
} from 'src/app/models/fiat-deposit';
import type { Coin } from 'src/app/models/coin';
import type { FileUpload } from 'src/app/models/file-upload';
import type { Pagination } from 'src/app/models/pagination';
import { SystemPart } from 'src/app/models/system-part';

@Injectable({
  providedIn: 'root'
})
export class FiatDepositService {

  depositQuery = `
    id
    code
    amount{amount currency}
    amountFee{amount currency}
    type
    typeName
    dateCreated
    dateDone
    dateCanceled
    status
    statusNote
    cashbackBonus{
      id
      code
      amount{amount currency}
      type
      typeName
      dateCreated
      dateDone
      dateCanceled
      status
      statusNote
      description
      note
    }
    penalty{
      amount{amount currency}
      description
    }
    cancelable
    origin
  `;

  additionalDataQuery = `
    additionalProviderData{
      ... on PaytabsAdditionalData{provider environment}
      ... on HyperPayAdditionalData{provider environment widgetUrl}
      ... on SafeChargeAdditionalData{
        provider
        environment
        gatewayUrl
        parameterString
        method
      }
      ... on CheckoutAdditionalData {
        provider
        environment
        publicKey
      }
    }
  `;

  fullDepositQuery = `
    ${this.depositQuery}
    ... on FiatDepositWire{
      transferId
      dateReceived
      recipientBank{
        accountName
        bankName
        branchName
        branchAddress
        accountNumber
        accountAddress
        iban
        swift
        correspondentBank{forCurrency text}
      }
      transferReference
      isTransferReceiptRequired
    }

    ... on FiatDepositCreditCard{
      dateReceived
      transferId
      gatewayTransactionId
      amountPaid{amount currency}
      gatewayFailureMessage
      ${this.additionalDataQuery}
    }
  `;

  private _paymentTypesObservable: Observable<PaymentTypeInfo[]>;

  constructor(private api: ApiService) { }

  get(id: string): Observable<FiatDeposit> {
    return this.api.get(`{
        fiatDeposit(id: ${id}){
          ${this.fullDepositQuery}
        }
      }`).pipe(
      switchMap((response) => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['fiatDeposit']);
      }),
      this.api.unifyErrors()
    );
  }

  list(first: number = 5, after: string = null, type: FiatDepositType = null): Observable<Pagination<FiatDeposit>> {
    return this.api.get(`{
      fiatDeposits(
        first: ${first}
        ${after ? ', after: "' + after + '"' : ''}
        ${type ? ', type: ' + after : ''}
      ){
        edges{
          node{
            ${this.fullDepositQuery}
          }
          cursor
        }
        pageInfo{
          startCursor
          endCursor
          hasNextPage
        }
        totalCount
      }
    }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['fiatDeposits']);
      }),
      this.api.unifyErrors()
    );
  }

  cancel(id: string): Observable<FiatDeposit> {
    return this.api.get(`mutation{
        cancelFiatDeposit(id: ${id}){
          ... on FiatDepositPayload{
            deposit{
              ${this.fullDepositQuery}
            }
          }
          ... on OperationError{
            errors{
              message
              field
            }
          }
        }
      }`).pipe(
      switchMap((response) => {
        if (response['data']['cancelFiatDeposit']['errors']) {
          return throwError(response['data']['cancelFiatDeposit']);
        }

        return of(response['data']['cancelFiatDeposit']['deposit']);
      }),
      this.api.unifyErrors()
    );
  }

  create(amount: Coin, type: FiatDepositType, instantDeposit = false, origin = SystemPart.EXCHANGE): Observable<FiatDeposit> {
    return this.api.get(`mutation{
        createFiatDeposit(
          amount: {
            amount: "${amount.amount}",
            currency: "${amount.currency}"
          }
          paymentType: ${type},
          origin: ${origin},
          instantDeposit: ${instantDeposit}
        ){
          ... on FiatDepositPayload{
            deposit{
              ${this.fullDepositQuery}
            }
          }
          ... on OperationError{
            errors{
              message
              field
            }
          }
        }
      }`).pipe(
      switchMap(response => {
        if (response['data']['createFiatDeposit']['errors']) {
          return throwError(response['data']['createFiatDeposit']);
        }

        return of(response['data']['createFiatDeposit']['deposit']);
      }),
      this.api.unifyErrors()
    );
  }

  confirmWire(id: string, transferReceipt: FileUpload): Observable<FiatDeposit> {
    return this.api.get(`mutation confirmFiatWireDeposit ($id: Int!, $transferReceipt: FileUploadInput) {
        confirmFiatWireDeposit(
          id: $id
          transferReceipt: $transferReceipt
        ) {
          ... on FiatDepositWire{
            ${this.depositQuery}
            recipientBank{
              accountName
              bankName
              branchName
              branchAddress
              accountNumber
              accountAddress
              iban
              swift
              correspondentBank{forCurrency text}
            }
            transferReference
            dateReceived
            transferId
          }
          ... on OperationError{
            errors{
              message
              field
            }
          }
        }
      }`, {
        id,
        transferReceipt
      }).pipe(
      switchMap(response => {
        if (response['data']['confirmFiatWireDeposit']['errors']) {
          return throwError(response['data']['confirmFiatWireDeposit']);
        }

        return of(response['data']['confirmFiatWireDeposit']);
      }),
      this.api.unifyErrors()
    );
  }

  confirmCreditCard(id: string, gatewayData: PaymentGatewayConfirmationInput = null): Observable<FiatDeposit | FiatCreditCardDepositRedirectResponse> {
    return this.api.get(`mutation confirmFiatCreditCardDeposit($id: Int! $confirmation: Boolean, $gatewayData: PaymentGatewayConfirmationInput) {
      confirmFiatCreditCardDeposit(
        id: $id,
        confirmation: $confirmation,
        gatewayData: $gatewayData
      ){
        ... on FiatDepositCreditCard{
          ${this.depositQuery}
          dateReceived
          transferId
          gatewayTransactionId
          amountPaid{amount currency}
          gatewayFailureMessage
          ${this.additionalDataQuery}
        }
        ... on FiatCreditCardDepositRedirectResponse {
          url
          successUrl
          failureUrl
        }

        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`, {
      id,
      confirmation: true,
      gatewayData
    }).pipe(
      switchMap(response => {
        if (response['data']['confirmFiatCreditCardDeposit']['errors']) {
          return throwError(response['data']['confirmFiatCreditCardDeposit']);
        }

        return of(response['data']['confirmFiatCreditCardDeposit']);
      }),
      this.api.unifyErrors()
    );
  }

  paymentTypes(): Observable<PaymentTypeInfo[]> {
    if (this._paymentTypesObservable) {
      return this._paymentTypesObservable;
    }

    this._paymentTypesObservable = this.api.get(`{
      paymentTypes{
        type
        supportedCurrencies{
          code
          name
          type
          currentExchangeRate{
            amount
            currency
          }
          allowedDecimals
          imageUrl
          colorCode
          sortOrder
          acceptedPairs
        }
        bankDetailsRequired
        providers
        notes {
          severity
          text
        }
        isInstantDepositAvailable
      }
    }`).pipe(
    switchMap((response) => {
      if (response['errors']) {
        return throwError(response);
      }

      const results = {};
      return of(response['data']['paymentTypes']);
    }),
    this.api.unifyErrors(this._paymentTypesObservable),
    share()
    );

    return this._paymentTypesObservable;
  }

  paymentType(type: PaymentType): Observable<PaymentTypeInfo> {
    return this.paymentTypes().pipe(
      map(response => {
        return response ? response.find(item => item.type === type) : null;
      })
    );
  }

  getFiatDepositFee(paymentType: PaymentType, amount: Coin, instantDeposit = false): Observable<FiatDepositFee> {
    return this.api.get(`query getFiatDepositFee($paymentType: PaymentType!, $amount: CoinInput!, $instantDeposit: Boolean!) {
      fiatDepositFee(paymentType:$paymentType,amount:$amount, instantDeposit:$instantDeposit) {
        ... on FiatDepositFee {
          fee{
            amount
            currency
          }
          amount{
            amount
            currency
          }
          totalAmount{
            amount
            currency
          }
        }
      }
    }`, {
      paymentType,
      amount,
      instantDeposit
    }).pipe(
      switchMap(response => {
          if (response['errors']) {
            return throwError(response);
          }
          return of(response['data']['fiatDepositFee']);
        }
      ),
      this.api.unifyErrors()
    );
  }
}
